import { Component, h, Prop } from "@stencil/core";
import lottie, { AnimationItem } from "lottie-web";

import { Bundle, bundleForLanguage } from "../../services/i18n";
import offerLottie from "./icons/offers.json";
import internetLottie from "./icons/internet.json";
import devicesLottie from "./icons/devices.json";
import mobileLottie from "./icons/mobile.json";
import tvLottie from "./icons/tv.json";
import insuranceLottie from "./icons/insurance.json";
import { TLanguage } from "../../types";
import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";

@Component({
  tag: "scs-content-navigation",
  styleUrls: ["content-navigation.scss"],
  shadow: true,
})
export class ContentNavigationComponent {
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;
  @Prop()
  public showMobileIcon = false;
  @Prop()
  public showOfferIcon = false;
  @Prop()
  public showInsuranceIcon = false;
  @Prop()
  public showTvIcon = false;
  @Prop()
  public showDevicesIcon = false;
  @Prop()
  public showInternetIcon = false;
  @Prop()
  public mobileHref = "";
  @Prop()
  public offerHref = "";
  @Prop()
  public insuranceHref = "";
  @Prop()
  public tvHref = "";
  @Prop()
  public devicesHref = "";
  @Prop()
  public internetHref = "";
  @Prop()
  public trackingEnabled = true;
  @Prop()
  public trackingLocation = "home";
  @Prop()
  public trackingAction = "link";
  @Prop()
  public trackingCategory = "content-navigation";

  private trackByEvent: (event: Event) => void;

  private i18n!: Bundle;

  private prefersReducedMotion: Boolean = false;

  private animations: Record<string, AnimationItem> = {};

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
  }

  public connectedCallback() {}

  public async componentWillLoad(): Promise<void> {
    this.i18n = await bundleForLanguage(this.language);
  }

  public async componentWillUpdate(): Promise<void> {}

  public async componentDidRender(): Promise<void> {}

  loadAnimation(id: string, data: any) {
    const domNode = document
      .querySelector("scs-content-navigation")
      ?.shadowRoot?.getElementById(id) as HTMLCanvasElement;
    domNode.textContent = "";

    this.animations[id] = lottie.loadAnimation({
      container: domNode,
      renderer: "svg",
      loop: false,
      autoplay: this.prefersReducedMotion ? false : true,
      animationData: data,
    });

    if (this.prefersReducedMotion) {
      // Ideally this would be last frame (animation complete) but some of our animations end on empty frames
      this.animations[id].goToAndStop(
        this.animations[id].getDuration(true) - 5,
        true,
      );
    }
  }

  replayAnimation(id: string) {
    if (this.prefersReducedMotion || !this.animations[id]) return;

    this.animations[id].goToAndPlay(this.animations[id].firstFrame, true);
  }

  componentDidLoad() {
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.showMobileIcon && this.loadAnimation("dolottie-mobile", mobileLottie);
    this.showOfferIcon && this.loadAnimation("dolottie-offer", offerLottie);
    this.showInsuranceIcon &&
      this.loadAnimation("dolottie-insurance", insuranceLottie);
    this.showTvIcon && this.loadAnimation("dolottie-tv", tvLottie);
    this.showDevicesIcon &&
      this.loadAnimation("dolottie-devices", devicesLottie);
    this.showInternetIcon &&
      this.loadAnimation("dolottie-internet", internetLottie);
  }

  public render() {
    return (
      <div class="wrapper">
        <div class="icon-container">
          {this.showMobileIcon && (
            <a
              href={this.mobileHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "mobile"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => {
                this.replayAnimation("dolottie-mobile");
              }}
            >
              <div class="lottie-animation" id="dolottie-mobile"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_MOBILE_DESCRIPTION")}
              </span>
            </a>
          )}

          {this.showInternetIcon && (
            <a
              href={this.internetHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "internet"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => this.replayAnimation("dolottie-internet")}
            >
              <div class="lottie-animation" id="dolottie-internet"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_INTERNET_DESCRIPTION")}
              </span>
            </a>
          )}

          {this.showTvIcon && (
            <a
              href={this.tvHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "tv"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => this.replayAnimation("dolottie-tv")}
            >
              <div class="lottie-animation" id="dolottie-tv"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_TV_DESCRIPTION")}
              </span>
            </a>
          )}

          {this.showDevicesIcon && (
            <a
              href={this.devicesHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "devices"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => this.replayAnimation("dolottie-devices")}
            >
              <div class="lottie-animation" id="dolottie-devices"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_DEVICES_DESCRIPTION")}
              </span>
            </a>
          )}

          {this.showInsuranceIcon && (
            <a
              href={this.insuranceHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "insurance"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => this.replayAnimation("dolottie-insurance")}
            >
              <div class="lottie-animation" id="dolottie-insurance"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_INSURANCE_DESCRIPTION")}
              </span>
            </a>
          )}

          {this.showOfferIcon && (
            <a
              href={this.offerHref}
              class="icon"
              onClick={this.trackByEvent}
              data-track-action={this.trackingEnabled && this.trackingAction}
              data-track-location={
                this.trackingEnabled && this.trackingLocation
              }
              data-track-label={this.trackingEnabled && "offer"}
              data-track-category={
                this.trackingEnabled && this.trackingCategory
              }
              onMouseEnter={() => this.replayAnimation("dolottie-offer")}
            >
              <div class="lottie-animation" id="dolottie-offer"></div>
              <span class="h4 icon-description">
                {this.i18n.t("CONTENT_NAVIGATION_ICON_OFFER_DESCRIPTION")}
              </span>
            </a>
          )}
        </div>
      </div>
    );
  }
}
